import { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import {
  AdminRewards,
  BaseERC20,
  BaseERC20Factory,
  JointCampaign,
  LiquidityCampaign,
  LiquidityCampaignV2,
  RewardCampaign,
} from '@arcxgame/contracts/dist/src/typings'
import { calculateAPY, nowInSeconds } from '@arcxmoney/arcx-core'
import dayjs from 'dayjs'
import { BigNumber, constants, Contract, ContractTransaction, Signer } from 'ethers'
import { Network } from 'src/config'
import { BASE } from 'src/constants/crypto'
import { getReadableNumber } from 'src/helpers/getReadableNumber'
import { CoingeckoApi } from 'src/services/CoingeckoApi'
import { GraphService, PositionWithSynth } from 'src/services/GraphService'
import { IPeriodEndDate } from './IPeriodEndDate'
import { IRewardPair } from './IRewardPair'

export default abstract class ArcFarm {
  constructor(
    public name: string,
    public platform: string,
    public pair: string,
    public pairLink: string,
    public note: string,
    public signer: Signer,
    public network: Network,
    public contract:
    | RewardCampaign
    | LiquidityCampaign
    | LiquidityCampaignV2
    | JointCampaign
    | AdminRewards,
    public client: ApolloClient<NormalizedCacheObject>,
  ) {}

  /* ========== Getters ========== */

  abstract hasDebtRequirement(): boolean

  getPair(): string {
    return this.pair
  }

  getIsRewardClaimable(): Promise<boolean> {
    return this.contract.tokensClaimable()
  }

  async isRewardPeriodFinished(): Promise<boolean> {
    const endDate: BigNumber = await this.contract.periodFinish()
    const isEnded = endDate.lte(nowInSeconds())

    return isEnded
  }

  async getApys(): Promise<BigNumber[]> {
    const isEnded = await this.isRewardPeriodFinished()

    if (isEnded) return []

    const [rewardsToken] = await this.getRewardTokens()

    const totalRewards = await rewardsToken.balanceOf(this.contract.address)
    const rewardDurationSecs = await this.contract.rewardsDuration()

    let stakedUSDValue = await this.contract.totalSupply()
    const daoAllocation = await this.contract.daoAllocation()

    const totalRewardsMinusDaoAllocation = totalRewards
      .mul(constants.WeiPerEther.sub(daoAllocation))
      .div(BASE)

    if (stakedUSDValue.isZero()) {
      return [constants.Zero]
    }

    // TODO: to remove once the new ARCX token is live on coingecko, plus need a cleaner approach for farms
    // which staking token is not = $1
    const priceOfRewardtoken = await CoingeckoApi.getUsdPrice(rewardsToken.address)

    // 1 staked token is not equal to 1 dollar, so gotta get the real price
    const usdPerEth = await CoingeckoApi.getUsdPrice('0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2')

    // Get amount of ETH per 1 slp
    const slpAbi = [
      'function getReserves() public view returns(uint112 _reserve0, uint112 _reserve1, uint32 _blockTimestampLast)',
    ]
    const slpContract = new Contract(
      '0x87C33321E1755ab54b0A27D4969A72B953486E75',
      slpAbi,
      this.signer,
    )
    const [arcxTokens, wethTokens] = await slpContract.getReserves()

    // Get the USD value of 1 slp
    stakedUSDValue = arcxTokens
      .mul(priceOfRewardtoken)
      .div(BASE)
      .add(wethTokens.mul(usdPerEth).div(BASE))

    return [
      calculateAPY(
        totalRewardsMinusDaoAllocation,
        rewardDurationSecs,
        priceOfRewardtoken,
        stakedUSDValue,
      ),
    ]
  }

  async getStakingCapString(): Promise<string> {
    return null
  }

  async getDebtRequirementString(): Promise<string> {
    return null
  }

  async getVestingEndDateString(): Promise<string> {
    return null
  }

  async getTotalRewardsString(): Promise<string> {
    const arcToken = BaseERC20Factory.connect(await this.contract.rewardsToken(), this.signer)

    const arcReward = await arcToken.balanceOf(this.contract.address)
    const arcSymbol = await arcToken.symbol()

    return `${getReadableNumber(arcReward)} ${arcSymbol}`
  }

  async getTotalRewards(): Promise<IRewardPair[]> {
    const arcToken = BaseERC20Factory.connect(await this.contract.rewardsToken(), this.signer)

    const arcSymbol = await arcToken.symbol()
    const arcReward = await arcToken.balanceOf(this.contract.address)

    return [
      {
        symbol: arcSymbol,
        amount: arcReward,
      },
    ]
  }

  async getRewardTokens(): Promise<BaseERC20[]> {
    const arcTokenAddress = await this.contract.rewardsToken()
    const arcToken = BaseERC20Factory.connect(arcTokenAddress, this.signer)

    return [arcToken]
  }

  async getEarningsString(): Promise<string> {
    const [graebersEarned] = await this.getEarnings()

    return `${getReadableNumber(graebersEarned.amount)} GRAEBERS`
  }

  async getEarnings(): Promise<IRewardPair[]> {
    const signerAddress = await this.signer.getAddress()
    const arcEarned = await this.contract.earned(signerAddress)
    const graebersEarned = arcEarned.mul(BigNumber.from(10).pow(6))

    return [
      {
        symbol: 'GRAEBERS',
        amount: graebersEarned,
      },
    ]
  }

  async getStakingToken(): Promise<BaseERC20 | null> {
    const stakingTokenAddy = await this.contract.stakingToken()
    return BaseERC20Factory.connect(stakingTokenAddy, this.signer)
  }

  async getStakingTokenBalance(): Promise<BigNumber> {
    const stakingToken = await this.getStakingToken()

    return stakingToken.balanceOf(await this.signer.getAddress())
  }

  async getStakedBalance(): Promise<BigNumber> {
    const signerAddress = await this.signer.getAddress()
    return this.contract.balanceOf(signerAddress)
  }

  async getPeriodFinish(): Promise<IPeriodEndDate[]> {
    const [rewardToken] = await this.getRewardTokens()
    const symbol = await rewardToken.symbol()
    const periodFinish: BigNumber = await this.contract.periodFinish()

    let endDateString: string

    if (periodFinish.isZero()) {
      endDateString = 'Not Started'
    } else {
      endDateString = dayjs(periodFinish.toNumber() * 1000).format('DD / MM / YYYY')
    }

    return [
      {
        symbol,
        endDateString,
      },
    ]
  }

  async getAvailableToStake(): Promise<BigNumber> {
    const stakingToken = await this.getStakingToken()

    const currentDebt = await this.getCurrentDebt()
    const debtToStakeRatio = BigNumber.from(await this.contract.stakeToDebtRatio())
    const stakedBalance = await this.getStakedBalance()
    const stakableBalance = await stakingToken.balanceOf(await this.signer.getAddress())

    return this._getAvailableToStake(
      currentDebt,
      debtToStakeRatio,
      stakedBalance,
      stakableBalance,
      false,
    )
  }

  async getCurrentDebtString(): Promise<string> {
    const borrowedAmount = await this.getCurrentDebt()

    return `${getReadableNumber(borrowedAmount)} STABLEx`
  }

  /**
   * Returns the highest debt of the relevant position
   */
  async getCurrentDebt(): Promise<BigNumber> {
    if (!this.hasDebtRequirement()) {
      return null
    }

    const largestPosition = await this.getLargestDebtPosition()

    if (largestPosition) {
      return largestPosition.borrowedAmountValue
    }
    return BigNumber.from(0)
  }

  /**
   * Returns the the position with the highest minted debt for the collateral of this farm
   */
  async getLargestDebtPosition(): Promise<PositionWithSynth> {
    if (!this.hasDebtRequirement()) {
      return null
    }

    const graphService = new GraphService(this.client)
    const stateContract = await this.contract.stateContract()
    const signerAddress = await this.signer.getAddress()

    const largestPosition = await graphService.fetchHighestPositionOfSynths(signerAddress, [
      stateContract,
    ])

    return largestPosition
  }

  /* ========== Modifiers ========== */

  abstract stake(amount: BigNumber): Promise<ContractTransaction>

  async withdraw(amount: BigNumber): Promise<ContractTransaction> {
    const withdrawTx = await this.contract.withdraw(amount)
    return withdrawTx
  }

  async exit(): Promise<ContractTransaction> {
    const exitTx = await this.contract.exit()
    return exitTx
  }

  async claimReward(): Promise<ContractTransaction> {
    try {
      const claimTx = await this.contract.getReward(await this.signer.getAddress())
      return claimTx
    } catch (err) {
      console.error(
        'Error while calling contract.getReward(address). Trying .getReward() for farm 1',
        err,
      )
    }

    try {
      const contract = new Contract(this.contract.address, ['function getReward()'], this.signer)
      const claimTx = await contract.getReward()
      return claimTx
    } catch (err) {
      console.error('Error calling contract.getReward()', err)
    }

    return null
  }

  /* ========== Protected methods ========== */

  /**
   * Helper method to compute the amount available to stake
   * @param multiplyRatio If true, will multiply the debt to the ratio. If false, will divide
   */
  protected _getAvailableToStake(
    currentDebt: BigNumber,
    debtToStakeRatio: BigNumber,
    stakedBalance: BigNumber,
    lpWalletBalance: BigNumber,
    multiplyRatio: boolean,
  ): BigNumber {
    if (!currentDebt || !debtToStakeRatio || !stakedBalance || !lpWalletBalance) {
      return BigNumber.from(0)
    }

    let maxAllowedLpTokensToStake: BigNumber

    if (multiplyRatio) {
      maxAllowedLpTokensToStake = currentDebt.mul(debtToStakeRatio)
    } else {
      maxAllowedLpTokensToStake = currentDebt.div(debtToStakeRatio)
    }

    const maxCanStake = maxAllowedLpTokensToStake.sub(stakedBalance)

    if (maxCanStake.lte(lpWalletBalance)) {
      return maxCanStake
    } else {
      return lpWalletBalance
    }
  }
}
